import type { FinPayrollBatchRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { formatDateRu, formatMoney } from '../financierFormat';
import {
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  BoldTitle,
  GreenProjectText,
  MutedText,
  PeriodText,
  BatchTypeText,
  NdflDeduction,
  CanteenDeduction,
  NetPayout,
  DiscrepancyBadge,
  SubExplanationText,
  InRegistryText,
} from './styled';
import { BATCH_TYPE_LABELS, BATCH_STATUS_CONFIG } from './types';

interface PayrollBatchesTableProps {
  batches: FinPayrollBatchRow[];
  onEnqueue: (batchId: string) => void;
  isEnqueuing: boolean;
  canWrite: boolean;
}

export function PayrollBatchesTable({
  batches,
  onEnqueue,
  isEnqueuing,
  canWrite,
}: PayrollBatchesTableProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th>Ведомость / Объект</th>
            <th>Период / Дата выплаты</th>
            <th>Тип выплаты</th>
            <th>Работников / Смен</th>
            <RightTh>Начислено / Удержания</RightTh>
            <RightTh>К выплате, ₽</RightTh>
            <th>Сверка с рапортами</th>
            <th>Статус</th>
            <RightTh>Действия</RightTh>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Зарплатные ведомости не найдены
              </TableNoticeTd>
            </tr>
          ) : (
            batches.map((b: FinPayrollBatchRow) => {
              const statusConf = BATCH_STATUS_CONFIG[b.status] ?? {
                label: b.status,
                tone: 'neutral',
              };
              const isReadyToEnqueue = b.status === 'verified_chronum';

              return (
                <tr key={b.id}>
                  <td>
                    <BoldTitle>{b.title}</BoldTitle>
                    <GreenProjectText>
                      {b.project_name}
                    </GreenProjectText>
                    <MutedText>
                      Банк: {b.salary_bank_name}
                    </MutedText>
                  </td>
                  <td>
                    <PeriodText>{b.period_month}</PeriodText>
                    <MutedText>
                      Выплата: {formatDateRu(b.payout_date)}
                    </MutedText>
                  </td>
                  <td>
                    <BatchTypeText>
                      {BATCH_TYPE_LABELS[b.batch_type] ?? b.batch_type}
                    </BatchTypeText>
                  </td>
                  <td>
                    <div><b>{b.employees_count}</b> чел.</div>
                    <MutedText>
                      {b.shifts_verified_count} смен
                    </MutedText>
                  </td>
                  <RightTd>
                    <div>{formatMoney(b.gross_accrued_amount)}</div>
                    <NdflDeduction>
                      НДФЛ: −{formatMoney(b.ndfl_amount)}
                    </NdflDeduction>
                    {b.canteen_deduction_amount > 0 && (
                      <CanteenDeduction>
                        Питание: −{formatMoney(b.canteen_deduction_amount)}
                      </CanteenDeduction>
                    )}
                  </RightTd>
                  <RightTd>
                    <NetPayout>
                      {formatMoney(b.net_payout_amount)}
                    </NetPayout>
                  </RightTd>
                  <td>
                    <DiscrepancyBadge $isOk={b.field_discrepancy_pct <= 2}>
                      {b.field_discrepancy_pct.toFixed(1)}% расхождение
                    </DiscrepancyBadge>
                    <SubExplanationText>
                      с рапортами прорабов
                    </SubExplanationText>
                  </td>
                  <td>
                    <Badge $tone={statusConf.tone}>{statusConf.label}</Badge>
                  </td>
                  <RightTd>
                    {isReadyToEnqueue && canWrite ? (
                      <Button
                        $variant="primary"
                        $size="sm"
                        disabled={isEnqueuing}
                        onClick={() => onEnqueue(b.id)}
                      >
                        В оплату (Гендиректор)
                      </Button>
                    ) : (
                      <InRegistryText>В реестре</InRegistryText>
                    )}
                  </RightTd>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}
